import {
  type DateValue,
  getLocalTimeZone,
  toCalendarDate,
  today,
} from "@internationalized/date"
import {
  type AriaCalendarCellProps,
  useCalendarCell,
} from "@react-aria/calendar"
import type { CalendarState, RangeCalendarState } from "@react-stately/calendar"
import clsx, { type ClassValue } from "clsx"
import React from "react"

import { Text } from "../Text"
import styles from "./CalendarCell.module.css"

interface Props {
  state: CalendarState | RangeCalendarState
  date: AriaCalendarCellProps["date"]
}

export function CalendarCell(props: Props) {
  const { state, date } = props

  const ref = React.useRef<HTMLDivElement>(null)

  const {
    cellProps,
    buttonProps,
    formattedDate,
    isFocused,
    isSelected,
    isDisabled,
    isOutsideVisibleRange,
  } = useCalendarCell({ date }, state, ref)

  const { cellClassName, textVariant } = getCellStyle({
    state,
    date,
    isFocused,
    isSelected,
    isDisabled,
    isOutsideVisibleRange,
  })

  return (
    <td {...cellProps} className={styles.root}>
      <div className={styles.wrapper} {...buttonProps} ref={ref}>
        <div className={cellClassName}>
          <Text variant={textVariant} color="inherit" align="center" noWrap>
            {formattedDate}
          </Text>
        </div>
      </div>
    </td>
  )
}

interface GetCellStyleArgs {
  state: CalendarState | RangeCalendarState
  date: DateValue
  isFocused: boolean
  isSelected: boolean
  isDisabled: boolean
  isOutsideVisibleRange: boolean
}

interface CellStyle {
  cellClassName: string
  textVariant: "bold" | "normal"
}

function getCellStyle(args: GetCellStyleArgs): CellStyle {
  const {
    state,
    date,
    isFocused,
    isSelected,
    isDisabled,
    isOutsideVisibleRange,
  } = args

  const isCurrentDay = isDateTheSameDay(date, today(getLocalTimeZone()))
  const isCommonBoldCondition = isFocused || isCurrentDay
  const isRangeSelection = "highlightedRange" in state

  const commonClasses: ClassValue[] = [
    styles.cell,
    isFocused && styles.focused,
    isDisabled && styles.disabled,
    isOutsideVisibleRange && styles.hidden,
    isCurrentDay && styles.currentDay,
  ]

  if (isRangeSelection) {
    return getRangeCellStyle({
      state,
      date,
      commonClasses,
      isCommonBoldCondition,
      isSelected,
    })
  }

  return {
    cellClassName: clsx(...commonClasses, isSelected && styles.selected),
    textVariant: isCommonBoldCondition || isSelected ? "bold" : "normal",
  }
}

interface RangeCellStyleArgs {
  state: RangeCalendarState
  date: DateValue
  commonClasses: ClassValue[]
  isCommonBoldCondition: boolean
  isSelected: boolean
}

function getRangeCellStyle(args: RangeCellStyleArgs): CellStyle {
  const { state, date, commonClasses, isCommonBoldCondition, isSelected } = args

  const isRangeBoundary = state.highlightedRange
    ? isDateInRangeBoundaries(date, state.highlightedRange)
    : false

  const isInRange = isSelected && !isRangeBoundary

  return {
    cellClassName: clsx(
      ...commonClasses,
      isRangeBoundary && styles.selected,
      isInRange && styles.inRange,
    ),
    textVariant: isCommonBoldCondition || isRangeBoundary ? "bold" : "normal",
  }
}

function isDateInRangeBoundaries(
  date: DateValue,
  range: { start: DateValue; end: DateValue },
): boolean {
  return (
    isDateTheSameDay(date, range.start) || isDateTheSameDay(date, range.end)
  )
}

function isDateTheSameDay(a: DateValue, b: DateValue) {
  return toCalendarDate(a).compare(toCalendarDate(b)) === 0
}
